import React, { useEffect, useMemo, useRef, useState } from "react";
import { Add as AddIcon, Delete as DeleteIcon, Info } from "@mui/icons-material";
import { Box, Button, Divider, IconButton, TextField, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Newspaper } from "lucide-react";
import { NewsItem } from "../../types";
import {
  ExperienceFieldGuide,
  ExperienceFieldLabel,
  ExperienceHeader,
  ExperienceMediaCard,
  ExperienceOptionalLabel,
  ExperienceShell,
  type ExperienceMomentVariant,
  getExperienceFieldSx,
  getExperienceTheme,
  getPrimaryButtonSx,
  isSeasonMomentVariant,
} from "./ExperienceUI";

interface NewsExperienceProps {
  darkMode: boolean;
  data?: NewsItem[];
  momentVariant?: ExperienceMomentVariant;
  onDataChange?: (data: NewsItem[]) => void;
}

const EMPTY_NEWS_ITEM: NewsItem = {
  title: "",
  description: "",
  url: "",
};

const normalizeNewsItems = (
  data: NewsItem[] | NewsItem | null | undefined,
  isSeason: boolean,
  defaultNews: Required<NewsItem>,
): NewsItem[] => {
  if (Array.isArray(data)) {
    return data.length > 0 ? data : [isSeason ? EMPTY_NEWS_ITEM : defaultNews];
  }
  if (data && (data.title || data.description || data.url)) {
    return [
      {
        title: data.title || "",
        description: data.description || "",
        url: data.url || "",
      },
    ];
  }
  return [isSeason ? EMPTY_NEWS_ITEM : defaultNews];
};

const NewsExperience: React.FC<NewsExperienceProps> = ({
  darkMode,
  data,
  momentVariant = "standard",
  onDataChange,
}) => {
  const { t } = useTranslation("taascard");
  const isSeason = isSeasonMomentVariant(momentVariant);
  const theme = getExperienceTheme(darkMode);
  const isInternalUpdateRef = useRef(false);
  const fieldSx = getExperienceFieldSx(darkMode);
  const defaultNews: Required<NewsItem> = {
    title: t("news_default_title"),
    description: t("news_default_description"),
    url: t("news_default_url"),
  };
  const [newsItems, setNewsItems] = useState<NewsItem[]>(() =>
    normalizeNewsItems(data, isSeason, defaultNews),
  );

  useEffect(() => {
    if (data && !isInternalUpdateRef.current) {
      const nextItems = normalizeNewsItems(data, isSeason, defaultNews);
      if (JSON.stringify(nextItems) !== JSON.stringify(newsItems)) {
        setNewsItems(nextItems);
      }
    }
    isInternalUpdateRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isSeason]);

  const updateNewsItems = (updater: NewsItem[] | ((prev: NewsItem[]) => NewsItem[])) => {
    setNewsItems((prev) => {
      const next =
        typeof updater === "function"
          ? (updater as (prev: NewsItem[]) => NewsItem[])(prev)
          : updater;
      if (onDataChange) {
        isInternalUpdateRef.current = true;
        onDataChange(next);
      }
      return next;
    });
  };

  const imagePreviewUrls = useMemo(
    () =>
      newsItems.map((item) => {
        if (!item.url) return "";
        if (item.url instanceof File) {
          return URL.createObjectURL(item.url);
        }
        return item.url;
      }),
    [newsItems]
  );

  useEffect(() => {
    return () => {
      imagePreviewUrls.forEach((url, idx) => {
        if (newsItems[idx]?.url instanceof File && url) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, [imagePreviewUrls, newsItems]);

  const handleNewsChange = (index: number, field: keyof NewsItem, value: string | File) => {
    updateNewsItems((prev) =>
      prev.map((item, i) => (i === index ? { ...item, [field]: value } : item))
    );
  };

  const addNewsItem = () => {
    updateNewsItems((prev) => [...prev, { title: "", description: "", url: "" }]);
  };

  const removeNewsItem = (index: number) => {
    updateNewsItems((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((_, i) => i !== index);
    });
  };

  return (
    <ExperienceShell>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 1,
        }}
      >
        {!isSeason && (
          <ExperienceHeader
            darkMode={darkMode}
            icon={Newspaper}
            title={t("news")}
            subtitle={t("news_header_subtitle")}
          />
        )}
        {!isSeason && (
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={addNewsItem}
            sx={{ ...getPrimaryButtonSx(darkMode), flexShrink: 0, mt: 0.5 }}
          >
            {t("add_news")}
          </Button>
        )}
      </Box>

      {!isSeason && (
        <Typography
          sx={{
            color: theme.textMuted,
            fontSize: "0.875rem",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Info fontSize="small" /> {t("news_latest_three_note")}
        </Typography>
      )}

      <ExperienceFieldGuide darkMode={darkMode}>
        {isSeason ? t("news_field_guide_season") : t("news_field_guide")}
      </ExperienceFieldGuide>

      {isSeason && newsItems.length < 3 && (
        <Button
          variant="text"
          size="small"
          startIcon={<AddIcon />}
          onClick={addNewsItem}
          sx={{ alignSelf: "flex-start", textTransform: "none" }}
        >
          {t("news_add_another_update")}
        </Button>
      )}

      {newsItems.map((item, index) => (
        <Box key={`news-item-${index}`} sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {index > 0 && <Divider sx={{ borderColor: theme.border }} />}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <ExperienceFieldLabel darkMode={darkMode}>
              {isSeason
                ? index === 0
                  ? t("news_season_update")
                  : t("news_update_n", { index: index + 1 })
                : t("news_item_label", { index: index + 1 })}
            </ExperienceFieldLabel>
            <IconButton
              size="small"
              onClick={() => removeNewsItem(index)}
              disabled={newsItems.length <= 1}
            >
              <DeleteIcon fontSize="small" sx={{ color: "var(--destructive-600)" }} />
            </IconButton>
          </Box>

          <TextField
            fullWidth
            label={t("news_headline")}
            placeholder={t("news_headline_placeholder")}
            value={item.title || ""}
            onChange={(e) => handleNewsChange(index, "title", e.target.value)}
            helperText={isSeason ? t("news_headline_helper_season") : undefined}
            sx={fieldSx}
          />

          <TextField
            fullWidth
            multiline
            minRows={3}
            label={
              isSeason
                ? t("news_description_label_season")
                : t("news_description_label")
            }
            placeholder={t("news_description_placeholder")}
            value={item.description || ""}
            onChange={(e) => handleNewsChange(index, "description", e.target.value)}
            helperText={
              isSeason
                ? t("news_description_helper_season")
                : t("news_description_helper")
            }
            sx={fieldSx}
          />

          <ExperienceFieldLabel darkMode={darkMode}>
            {t("image")}
            {isSeason && <ExperienceOptionalLabel darkMode={darkMode} />}
          </ExperienceFieldLabel>
          <Button
            variant="contained"
            component="label"
            sx={{ ...getPrimaryButtonSx(darkMode), alignSelf: "flex-start" }}
          >
            {item.url ? t("replace_image") : t("upload_image")}
            <input
              type="file"
              accept="image/*"
              hidden
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                const reader = new FileReader();
                reader.onloadend = () => {
                  const result = reader.result as string;
                  handleNewsChange(index, "url", result);
                };
                reader.readAsDataURL(file);
                e.target.value = "";
              }}
            />
          </Button>
          {imagePreviewUrls[index] && (
            <ExperienceMediaCard darkMode={darkMode}>
              <Box
                component="img"
                src={imagePreviewUrls[index]}
                alt={t("news_image_alt", { index: index + 1 })}
                sx={{ width: "100%", maxWidth: 360, display: "block" }}
              />
            </ExperienceMediaCard>
          )}
        </Box>
      ))}
    </ExperienceShell>
  );
};

export default NewsExperience;
